// The integrations on a project card: [Autodesk logo][settings] [Procore logo][settings].
// Each logo is in colour when the user's account is connected, with a green check when
// this project is linked (ACC: a Revit file; Procore: a Procore project). The ACC logo
// opens the Revit link window, the Procore logo the Procore project link, and each
// settings icon that provider's delivery folders.
import React, { useState } from "react";
import { MDBIcon } from "mdbreact";
import AccLinkButton from "components/Acc/AccLinkButton";
import IntegrationSettingsModal from "./IntegrationSettingsModal";
import { ProcoreLogo } from "./ProviderLogos";
import { accountsText } from "./AccountsCount";
import { useIntegrationCard, useProviderStatus } from "./useIntegrations";
import "./integrations.scss";

export default function IntegrationCardButtons({ projectId, projectTitle, canEdit }) {
  const [open, setOpen] = useState(null); // { provider, startOnLink }
  const card = useIntegrationCard(projectId);
  const procore = useProviderStatus("procore");
  const acc = useProviderStatus("acc");
  const pc = (card && card.procore) || {};
  const ac = (card && card.acc) || {};
  // CONNECTED ON ANY PROJECT (owner 2026-10-25): in colour when one of my accounts works -
  // not only the first - or one the project's destinations use does
  const anyOn = (status, x) => !!(status && (status.connected || (status.accounts || []).some((a) => a.connected))) || (x.accountsConnected || 0) > 0;
  const pcOn = anyOn(procore, pc);
  const accOn = anyOn(acc, ac);
  const folders = (n) => (n ? `${n} delivery folder${n === 1 ? "" : "s"} set` : "no delivery folders set");
  // THE DELIVERY FOLDERS ARE NOT EVERYONE'S TO CHANGE (owner 2026-10-19):
  // the logo and its settings gear belong to a project owner, a company admin
  // or an admin. The server says so on each provider's /status (`allowed`),
  // and an undefined flag counts as allowed so an older server behaves as it
  // always did. A gear that opens a window whose every write 403s is worse
  // than no gear.
  const canAcc = !(acc && acc.allowed === false);
  const canProcore = !(procore && procore.allowed === false);
  // hovering the Procore logo reads the connection and where models and shop drawings go
  const pcPaths = pc.paths || {};
  const procoreTitle = [
    pcOn ? `Procore: connected${procore && procore.connected && procore.email ? ` as ${procore.email}` : ""}` : "Procore: not connected",
    pc.linked ? `Linked to ${pc.linkPath}` : "Not linked to a Procore project",
    pc.actorEmail ? `Uploads and downloads as ${pc.actorEmail}` : "",
    `Models: ${pcPaths.models || "not set"}`,
    `Shop drawings: ${pcPaths.shopDrawings || "not set"}`,
  ].filter(Boolean).join("\n");

  // HOW MANY ACCOUNTS THE PROJECT USES per provider (owner 2026-10-25), between the logo
  // and its gear; hovering it names them and whether each works
  const accountsCell = (x, name) => {
    const list = x.accountList || [];
    const n = x.accounts || 0;
    const broken = (x.accountsConnected || 0) < n;
    const title = n
      ? [`${name}: ${accountsText(list)}`, ...list.map((a) => `${a.label}${a.email && a.email !== a.label ? ` (${a.email})` : ""} - ${a.connected ? "connected" : "not connected"}`)].join("\n")
      : `${name}: no account used on this project yet`;
    return (
      <span className={`pc-accounts-cell ${n ? (broken ? "broken" : "") : "none"}`} title={title} data-testid={`card-accounts-${name.toLowerCase()}`}>
        {n}
      </span>
    );
  };

  return (
    <>
      {canAcc && (
      <span className="pc-integration">
        <AccLinkButton projectId={projectId} projectTitle={projectTitle} canEdit={canEdit} connected={accOn} />
        {accountsCell(ac, "ACC")}
        <button
          className="pc-integration-gear"
          title={`ACC settings - ${folders(ac.targetsSet)}`}
          onClick={() => setOpen({ provider: "acc" })}
        >
          <MDBIcon icon="cog" />
        </button>
      </span>
      )}
      {/* shown even before Procore is set up on the server: the window says so */}
      {canProcore && (
        <span className="pc-integration">
          <button
            className={`pc-tool ${pc.linked ? "linked procore" : ""}`}
            title={procoreTitle}
            onClick={() => setOpen({ provider: "procore", startOnLink: true })}
          >
            <ProcoreLogo connected={pcOn} linked={!!pc.linked} size={22} />
          </button>
          {accountsCell(pc, "Procore")}
          <button
            className="pc-integration-gear"
            title={`Procore settings - ${folders(pc.targetsSet)}`}
            onClick={() => setOpen({ provider: "procore" })}
          >
            <MDBIcon icon="cog" />
          </button>
        </span>
      )}
      {open ? (
        <IntegrationSettingsModal
          provider={open.provider}
          startOnLink={!!open.startOnLink}
          projectId={projectId}
          projectTitle={projectTitle}
          canEdit={canEdit}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </>
  );
}
